import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Pencil, Trash2, X, Save, Search, ChevronLeft, ChevronRight, Users, Download, Upload, FileSpreadsheet, ChevronDown } from 'lucide-react';
import CreateButton from '../components/CreateButton';

const PERMISSION_LABELS = {
  6: { label: 'Super Admin', bg: 'bg-purple-100', text: 'text-purple-700' },
  1: { label: 'Admin', bg: 'bg-red-100', text: 'text-red-700' },
  2: { label: 'Operador', bg: 'bg-blue-100', text: 'text-blue-700' },
  3: { label: 'Comercial', bg: 'bg-green-100', text: 'text-green-700' },
  5: { label: 'Cliente', bg: 'bg-yellow-100', text: 'text-yellow-700' },
};

/* ── Client autocomplete for user→client association ── */
const ClientAutocomplete = ({ value, displayName, onChange, onClear, token, API_URL }) => {
  const [query, setQuery] = useState(displayName || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(displayName || ''); }, [displayName]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/clients/search-all?q=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.clients || []);
      }
    } catch { /* ignore */ }
    setSearching(false);
  }, [API_URL, token]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowDropdown(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (client) => {
    onChange(client.id, client.razon_social);
    setQuery(client.razon_social);
    setShowDropdown(false);
    setResults([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (results.length) setShowDropdown(true); }}
          placeholder="Buscar cliente..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md rounded-r-none text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {value ? (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); onClear(); }}
            className="px-3 border border-l-0 border-gray-300 rounded-r-md text-gray-400 hover:text-red-500 bg-white"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      {showDropdown && (results.length > 0 || searching) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-sm text-gray-400">Buscando...</div>}
          {results.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSelect(c)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors"
            >
              <span className="font-medium text-gray-800">{c.razon_social}</span>
              <span className="text-gray-400 ml-2">#{c.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UserManagement = () => {
  const { token, API_URL, user } = useAuth();
  const isSuperAdmin = user?.permisos === 6;
  const fileInputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ username: '', password: '', Nombrec: '', email: '', permisos: 5, clinica_matriz: 0 });
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showBulkResult, setShowBulkResult] = useState(false);
  const [bulkSection, setBulkSection] = useState({ created: true, updated: false, errors: true });
  const [clientDisplayName, setClientDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar usuarios');
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm({ username: '', password: '', Nombrec: '', email: '', permisos: 5, clinica_matriz: 0 });
    setClientDisplayName('');
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ username: user.username, password: '', Nombrec: user.Nombrec, email: user.email || '', permisos: user.permisos, clinica_matriz: user.clinica_matriz });
    setClientDisplayName(user.cliente_nombre || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const url = editingUser ? `${API_URL}/users/${editingUser.id}` : `${API_URL}/users`;
      const method = editingUser ? 'PUT' : 'POST';

      const body = { ...form };
      if (editingUser && !body.password) delete body.password;

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');

      setShowModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await fetch(`${API_URL}/users/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar plantilla');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usuarios_plantilla_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleBulkUpload = async (file) => {
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/users/bulk-excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al procesar Excel');
      setBulkResult(data);
      setShowBulkResult(true);
      fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    try {
      const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.Nombrec || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0099FF' }}></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">

      {/* Header bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Users className="w-6 h-6" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gestión de Usuarios</h1>
              <p className="text-sm text-gray-500">Administración de cuentas y permisos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <>
                <button
                  onClick={downloadTemplate}
                  disabled={downloadingTemplate}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Descargar plantilla Excel"
                >
                  {downloadingTemplate
                    ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    : <Download size={14} />}
                  Plantilla
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={bulkUploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Subir Excel de usuarios"
                >
                  {bulkUploading
                    ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    : <Upload size={14} />}
                  Subir Excel
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleBulkUpload(f);
                  }}
                />
              </>
            )}
            <CreateButton onClick={openCreate}>Nuevo Usuario</CreateButton>
          </div>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mt-4 flex items-center justify-between">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre de usuario o nombre completo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table (scrollable) */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers.map((user) => {
                const perm = PERMISSION_LABELS[user.permisos];
                return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.Nombrec}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {perm ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${perm.bg} ${perm.text}`}>
                          {perm.label}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Nivel {user.permisos}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" title={user.clinica_matriz ? `ID: ${user.clinica_matriz}` : ''}>
                      {user.cliente_nombre || (user.clinica_matriz ? `#${user.clinica_matriz}` : '-')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(user)}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Users size={36} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">No se encontraron registros</p>
                    <p className="text-xs text-gray-400 mt-1">Intente con otros criterios de búsqueda</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} de {filteredUsers.length} registros
            </p>
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Anterior
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    style={currentPage === pageNum ? { backgroundColor: '#0099FF' } : {}}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                Siguiente <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuario *</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña {editingUser ? '(vacío = sin cambios)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input
                    type="text"
                    value={form.Nombrec}
                    onChange={(e) => setForm({ ...form, Nombrec: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="usuario@correo.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                  <select
                    value={form.permisos}
                    onChange={(e) => setForm({ ...form, permisos: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={6}>Super Admin</option>
                    <option value={1}>Administrador</option>
                    <option value={2}>Operador</option>
                    <option value={3}>Comercial</option>
                    <option value={5}>Cliente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente asociado</label>
                  <ClientAutocomplete
                    value={form.clinica_matriz}
                    displayName={clientDisplayName}
                    onChange={(id, name) => {
                      setForm({ ...form, clinica_matriz: id });
                      setClientDisplayName(name);
                    }}
                    onClear={() => {
                      setForm({ ...form, clinica_matriz: 0 });
                      setClientDisplayName('');
                    }}
                    token={token}
                    API_URL={API_URL}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#0099FF' }}
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
                {editingUser ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Result Modal */}
      {showBulkResult && bulkResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={20} style={{ color: '#0099FF' }} />
                <h3 className="text-lg font-semibold text-gray-900">Resultado de la carga</h3>
              </div>
              <button onClick={() => setShowBulkResult(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs text-green-700 font-medium uppercase tracking-wide">Creados</p>
                  <p className="text-2xl font-bold text-green-700">{bulkResult.created?.length || 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium uppercase tracking-wide">Actualizados</p>
                  <p className="text-2xl font-bold text-blue-700">{bulkResult.updated?.length || 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs text-red-700 font-medium uppercase tracking-wide">Errores</p>
                  <p className="text-2xl font-bold text-red-700">{bulkResult.errors?.length || 0}</p>
                </div>
              </div>

              {['created', 'updated', 'errors'].map((key) => {
                const list = bulkResult[key] || [];
                if (list.length === 0) return null;
                const titles = { created: 'Creados', updated: 'Actualizados', errors: 'Errores' };
                const open = bulkSection[key];
                return (
                  <div key={key} className="mb-3 border border-gray-200 rounded-md">
                    <button
                      onClick={() => setBulkSection({ ...bulkSection, [key]: !open })}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <span>{titles[key]} ({list.length})</span>
                      <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div className="border-t border-gray-200 max-h-60 overflow-y-auto">
                        {list.map((item, i) => (
                          <div key={i} className="px-4 py-2 text-xs text-gray-600 border-b border-gray-100 last:border-0">
                            {key === 'errors' ? (
                              <div>
                                <span className="font-medium text-red-600">Fila {item.row ?? '?'}:</span>{' '}
                                <span>{item.error || item.message || JSON.stringify(item)}</span>
                                {item.username && <span className="text-gray-400"> ({item.username})</span>}
                              </div>
                            ) : (
                              <div>
                                <span className="font-medium text-gray-800">{item.username || item.email || `#${item.id}`}</span>
                                {item.Nombrec && <span className="text-gray-500"> — {item.Nombrec}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowBulkResult(false)}
                style={{ backgroundColor: '#0099FF' }}
                className="px-4 py-2 rounded-md text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
